import React, { useMemo } from 'react';
import { formatCurrencyInt as formatCurrency } from '../../utils/formatters';
import { generateTbbcPDF } from '../../utils/exporters';
import { IconDividido, IconUnitario } from '../common/ModoIcons';

/**
 * Tabla de costos TBBC: filas = aplicaciones (con su valor), columnas = proyectos.
 * Cada celda es una casilla: marcada = el proyecto usa la app. Según el modo:
 *  - 'dividido': el valor se reparte en partes iguales entre los proyectos
 *    marcados (cada casilla vale 100/Nº %; "Reparto" = 100%).
 *  - 'unitario': cada proyecto marcado paga el valor completo ("Reparto" = ×Nº).
 * Las columnas de la derecha muestran lo que paga cada proyecto (c/cliente) y el
 * total de la app; al pie se suma lo que paga cada proyecto.
 */
export const TbbcMatriz = ({ proyectos, apps, asignaciones, onToggle, showNotification }) => {
  const handleExportPDF = () => {
    generateTbbcPDF({
      proyectos,
      apps,
      asignaciones,
      onSuccess: (t, m) => showNotification?.('success', t, m),
      onError: (t, m) => showNotification?.('error', t, m)
    });
  };

  const proyectoTotales = useMemo(() => {
    const tot = {};
    proyectos.forEach((p) => {
      tot[p.id] = 0;
    });
    apps.forEach((a) => {
      const row = asignaciones[a.id] || {};
      const count = proyectos.reduce((n, p) => n + (row[p.id] ? 1 : 0), 0);
      if (count === 0) return;
      // dividido: cada proyecto paga su parte; unitario: cada uno paga el valor.
      const parte = a.modo === 'unitario' ? a.costo : a.costo / count;
      proyectos.forEach((p) => {
        if (row[p.id]) tot[p.id] += parte;
      });
    });
    return tot;
  }, [proyectos, apps, asignaciones]);

  const granTotal = useMemo(
    () => Object.values(proyectoTotales).reduce((s, v) => s + v, 0),
    [proyectoTotales]
  );

  if (apps.length === 0) {
    return (
      <section className="proceso-matriz-section">
        <header className="proceso-matriz-header">
          <div className="proceso-matriz-titulo">
            <span className="proceso-matriz-nombre">Costo por proyecto</span>
          </div>
        </header>
        <p className="proceso-hint">
          Crea aplicaciones en la columna 2 para asignarlas a los proyectos.
        </p>
      </section>
    );
  }

  return (
    <section className="proceso-matriz-section">
      <header className="proceso-matriz-header">
        <div className="proceso-matriz-titulo">
          <span className="proceso-matriz-nombre">Costo por proyecto</span>
          <span className="proceso-matriz-tip">
            {apps.length} app{apps.length === 1 ? '' : 's'} × {proyectos.length} proyecto
            {proyectos.length === 1 ? '' : 's'} · marca quién usa cada app · ÷ reparte el valor, × lo cobra completo a cada uno
          </span>
        </div>
        <button className="proceso-pdf-btn" onClick={handleExportPDF}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Exportar informe PDF
        </button>
      </header>

      {proyectos.length === 0 ? (
        <p className="proceso-hint">Crea proyectos en la columna 1 para asignarles apps.</p>
      ) : (
        <div className="proceso-matriz-wrap">
          <table className="proceso-matriz tbbc-matriz">
            <thead>
              <tr>
                <th className="tz-app">Aplicación</th>
                <th className="tz-costo">Valor</th>
                {proyectos.map((p) => (
                  <th key={p.id} className="mz-cli" title={p.name}>{p.name}</th>
                ))}
                <th className="mz-suma" title="Dividido: suma 100%. Unitario: cada cliente paga el valor completo (×Nº)">Reparto</th>
                <th className="mz-cu" title="Lo que paga cada cliente marcado">c/cliente</th>
                <th className="mz-prorr" title="Total que cuesta la app">Total</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => {
                const row = asignaciones[a.id] || {};
                const count = proyectos.reduce((n, p) => n + (row[p.id] ? 1 : 0), 0);
                const isDiv = a.modo !== 'unitario';
                const pct = count > 0 ? Math.round(100 / count) : 0;
                const porCliente = count > 0 ? (isDiv ? a.costo / count : a.costo) : 0;
                const total = count > 0 ? (isDiv ? a.costo : a.costo * count) : 0;
                return (
                  <tr key={a.id}>
                    <td className="tz-app" title={`${a.name} · ${isDiv ? 'Dividido' : 'Unitario'}`}>
                      <span className={`tbbc-modo-badge ${isDiv ? 'div' : 'uni'}`} title={isDiv ? 'Dividido · reparte el valor entre los marcados' : 'Unitario · valor completo a cada uno'}>
                        {isDiv ? <IconDividido /> : <IconUnitario />}
                      </span>
                      {a.name}
                    </td>
                    <td className="tz-costo">{formatCurrency(a.costo)}</td>
                    {proyectos.map((p) => (
                      <td key={p.id} className="mz-cli mz-check">
                        <label className="tbbc-cell">
                          <input
                            type="checkbox"
                            className="tbbc-check"
                            checked={!!row[p.id]}
                            onChange={(e) => onToggle(a.id, p.id, e.target.checked)}
                          />
                          {row[p.id] && isDiv && <span className="tbbc-pct">{pct}%</span>}
                          {row[p.id] && !isDiv && <span className="tbbc-pct tbbc-pct-uni">1×</span>}
                        </label>
                      </td>
                    ))}
                    <td className={`mz-suma ${count > 0 ? 'ok' : ''}`}>
                      {count === 0 ? '—' : (isDiv ? '100%' : `×${count}`)}
                    </td>
                    <td className="mz-cu">
                      {count > 0 ? formatCurrency(porCliente) : '—'}
                    </td>
                    <td className={`mz-prorr ${count > 0 ? 'ok' : ''}`}>
                      {count > 0 ? formatCurrency(total) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="tz-app">Total por proyecto</td>
                <td className="tz-costo" />
                {proyectos.map((p) => (
                  <td key={p.id} className="mz-cli">
                    {formatCurrency(proyectoTotales[p.id] || 0)}
                  </td>
                ))}
                <td className="mz-suma" />
                <td className="mz-cu" />
                <td className="mz-prorr">{formatCurrency(granTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
};
